/**
 * Pure caret/array math behind the formula chip builders' in-place editing
 * (`FormulaBuilder.tsx`, `CellFormulaBuilder.tsx`, `LibraryFormulaBuilder.tsx`
 * via `useFormulaChipEditor`). Kept free of React so it can be unit-tested —
 * see `formulaTokens.test.ts`.
 *
 * A caret position is an index into the gaps around `tokens`: 0 is before the
 * first token, `tokens.length` is after the last. `null` means "no caret
 * placed" — inserts append to the end, matching the builders' original
 * always-append behavior.
 */

export type Caret = number | null

/** Keeps a caret in range after `tokens` changes length (e.g. an external
 *  formula edit outside the chip builder). Non-null caret only. */
export function clampCaret(caret: number, length: number): number {
  return Math.max(0, Math.min(caret, length))
}

/**
 * Splice `newTokens` in at `caret` (append when `caret` is null). Returns the
 * updated token list and the caret position just after the inserted run, so
 * repeated inserts continue from where the last one left off.
 */
export function insertTokensAt(
  tokens: string[],
  caret: Caret,
  newTokens: string[],
): { tokens: string[]; caret: number } {
  const at = caret === null ? tokens.length : clampCaret(caret, tokens.length)
  const next = [...tokens.slice(0, at), ...newTokens, ...tokens.slice(at)]
  return { tokens: next, caret: at + newTokens.length }
}

/** Swap the token at `index` for `newToken`. Every other token is untouched. */
export function replaceTokenAt(tokens: string[], index: number, newToken: string): string[] {
  if (index < 0 || index >= tokens.length) return tokens
  const next = tokens.slice()
  next[index] = newToken
  return next
}

/** Remove exactly the token at `index`. Caret lands where it was (clamped). */
export function removeTokenAt(tokens: string[], index: number): { tokens: string[]; caret: number } {
  if (index < 0 || index >= tokens.length) return { tokens, caret: clampCaret(index, tokens.length) }
  const next = [...tokens.slice(0, index), ...tokens.slice(index + 1)]
  return { tokens: next, caret: clampCaret(index, next.length) }
}

/** Remove the token immediately before `caret` (or the last token when
 *  `caret` is null) — the existing "backspace button" behavior, unchanged. */
export function backspaceTokens(tokens: string[], caret: Caret): { tokens: string[]; caret: number } {
  const at = caret === null ? tokens.length : clampCaret(caret, tokens.length)
  if (at === 0) return { tokens, caret: at }
  const next = [...tokens.slice(0, at - 1), ...tokens.slice(at)]
  return { tokens: next, caret: at - 1 }
}
